import { useState } from "react"
import { Box, Text, VStack, HStack, Button } from "@chakra-ui/react"
import { Link } from "@hydrologiq/shared/components/Link"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import StatusBadge from "./StatusBadge"
import { useCopyValues } from "../providers/helpers/useCopy"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "../copy/constants"
import AssetLink from "../asset/AssetLink"
import { useIsSectionEnabled } from "../providers/SectionFeatures"

interface CurrentOrderDialogProps {
  order?: {
    id: string
    name: string
    status: gqlTypes.OrderStatus
    start: string
    end: string
    quantity?: number | null
    quantity_consumed?: number | null
    customer?: { id: string; name: string } | null
    site?: { id: string; name: string } | null
    sites?: Array<{
      customer: { id: string; name: string }
      site: { id: string; name: string }
    }>
    assets?: Array<{
      id: string
      name: string
      kind: string
      status: string
      category: string
      class: string
      volume?: number | null
    }>
  }
  /**
   * Optional asset ID - if provided, shows a link to view all orders for this asset
   */
  assetId?: string
  /**
   * Optional customer ID - if provided, shows a link to view all orders for this customer
   */
  customerId?: string
  /**
   * Optional external control - if provided, dialog is controlled externally
   * If not provided, uses internal state with trigger (backwards compatible)
   */
  open?: boolean
  /**
   * Optional callback when dialog open state changes
   */
  onOpenChange?: (open: boolean) => void
  /**
   * Optional custom trigger element - only used when open/onOpenChange are not provided
   */
  trigger?: React.ReactNode
  /**
   * Optional flag to show the "show more" link
   */
  showMoreLink?: boolean
}

/**
 * Dialog component that displays current order information
 * Can be used for both assets and customers
 * Provides a link to view all orders for the asset or customer
 *
 * Supports two modes:
 * 1. Uncontrolled (backwards compatible): Pass no open/onOpenChange, uses internal state with trigger
 * 2. Controlled: Pass open and onOpenChange to control externally
 */
function CurrentOrderDialog({
  order,
  assetId,
  customerId,
  open: externalOpen,
  onOpenChange,
  trigger: customTrigger,
  showMoreLink = true,
}: CurrentOrderDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isCustomersEnabled = useIsSectionEnabled("customers")

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.CURRENT_ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.CURRENT_ORDER],
    [COPY_KEYS.HEADINGS.ORDER]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDER],
    [COPY_KEYS.HEADINGS.ORDERS]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ORDERS],
  })

  // Use external control if provided, otherwise use internal state
  const isControlled = externalOpen !== undefined && onOpenChange !== undefined
  const isOpen = isControlled ? externalOpen : internalOpen
  const setIsOpen = isControlled ? onOpenChange : setInternalOpen

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  // Determine the "show all orders" link based on context
  const getShowAllOrdersLink = () => {
    if (assetId) {
      return `/orders?asset=${assetId}`
    }
    if (customerId) {
      return `/orders?customer=${customerId}`
    }
    return `/orders?order=${order?.id}`
  }

  const getShowAllOrdersText = () => {
    if (assetId) {
      return `Show all ${copy[COPY_KEYS.HEADINGS.ORDERS].toLowerCase()} for this asset`
    }
    if (customerId) {
      return `Show all ${copy[COPY_KEYS.HEADINGS.ORDERS].toLowerCase()} for this customer`
    }
    return `Show ${copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} details`
  }

  // Default trigger for backwards compatibility
  const defaultTrigger = (
    <Link variant="primary" cursor="pointer" target="_blank">
      {order?.name}
    </Link>
  )

  return (
    <Dialog
      title={copy[COPY_KEYS.HEADINGS.CURRENT_ORDER]}
      open={isOpen}
      setOpen={setIsOpen}
      loading={false}
      disabled={false}
      trigger={!isControlled ? customTrigger || defaultTrigger : undefined}
      footer={
        <Button variant="hydrologiq_secondary" onClick={() => setIsOpen(false)}>
          Close
        </Button>
      }
    >
      {order && (
        <VStack align="stretch" gap={4} width="100%">
          <Box>
            <Text fontSize="sm" color="fg.muted">
              Showing {copy[COPY_KEYS.HEADINGS.ORDER].toLowerCase()} <strong>{order.name}</strong>
            </Text>
          </Box>

          <VStack align="stretch" gap={2}>
            <HStack>
              <Text fontWeight="semibold" minW="100px">
                Status:
              </Text>
              <StatusBadge
                status={
                  order.status === gqlTypes.OrderStatus.Scheduled
                    ? "scheduled"
                    : order.status === gqlTypes.OrderStatus.Started
                      ? "in_progress"
                      : order.status === gqlTypes.OrderStatus.Billed
                        ? "billed"
                        : "completed"
                }
              />
            </HStack>

            <HStack>
              <Text fontWeight="semibold" minW="100px">
                Start Date:
              </Text>
              <Text>{formatDate(order.start)}</Text>
            </HStack>

            <HStack>
              <Text fontWeight="semibold" minW="100px">
                End Date:
              </Text>
              <Text>{formatDate(order.end)}</Text>
            </HStack>

            {/* Customer information - hide when in customer view */}
            {!customerId && order.sites && order.sites.length > 0 && (
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Customer:
                </Text>
                <Link href={`/customers/${order.sites[0].customer.id}`} variant="primary" target="_blank" deLink={!isCustomersEnabled}>
                  {order.sites[0].customer.name}
                </Link>
              </HStack>
            )}

            {/* Fallback to legacy customer field if sites array not available - hide when in customer view */}
            {!customerId && (!order.sites || order.sites.length === 0) && order.customer && (
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Customer:
                </Text>
                <Link href={`/customers/${order.customer.id}`} variant="primary" deLink={!isCustomersEnabled}>
                  {order.customer.name}
                </Link>
              </HStack>
            )}

            {/* Site information - always show */}
            {order.sites && order.sites.length > 0 && (
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Site:
                </Text>
                <Link
                  href={`/customers/${order.sites[0].customer.id}/sites/${order.sites[0].site.id}`}
                  variant="primary"
                  deLink={!isCustomersEnabled}
                >
                  {order.sites[0].site.name}
                </Link>
              </HStack>
            )}

            {/* Fallback to legacy site field if sites array not available - always show */}
            {(!order.sites || order.sites.length === 0) && order.site && (
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Site:
                </Text>
                <Link href={`/customers/${order.customer?.id || "unknown"}/sites/${order.site.id}`} variant="primary" deLink={!isCustomersEnabled}>
                  {order.site.name}
                </Link>
              </HStack>
            )}

            {/* Asset information - hide when in asset view */}
            {!assetId && order.assets && order.assets.length > 0 && (
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Asset:
                </Text>
                <VStack align="start" gap={1}>
                  {order.assets.map((asset) => (
                    <AssetLink asset={asset} openExternal={true} key={asset.id} />
                  ))}
                </VStack>
              </HStack>
            )}

            {order.quantity != null && (
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Quantity (kg):
                </Text>
                <Text>{order.quantity.toFixed(2)}</Text>
              </HStack>
            )}

            {order.quantity_consumed != null && (
              <HStack>
                <Text fontWeight="semibold" minW="100px">
                  Quantity Consumed (kg):
                </Text>
                <Text>{order.quantity_consumed.toFixed(2)}</Text>
              </HStack>
            )}
          </VStack>

          {showMoreLink && (
            <Box pt={2} borderTopWidth="1px">
              <Link href={getShowAllOrdersLink()} variant="primary" target="_blank">
                {getShowAllOrdersText()}
              </Link>
            </Box>
          )}
        </VStack>
      )}
    </Dialog>
  )
}

export default CurrentOrderDialog
